import PaginatedList from "@theme/paginated-list";

/**
 * A custom element that renders a paginated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {MutationObserver | undefined} */
  #gridMutationObserver;

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute("initialized", "");

    // Initial check
    this.#updateLoadMoreButtonVisibility();

    // Observe the grid for new cards being added (handles infinite scroll)
    this.#setupGridMutationObserver();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#gridMutationObserver) {
      this.#gridMutationObserver.disconnect();
    }
  }

  /**
   * Setup MutationObserver on the grid to detect when new cards are added
   * This properly handles infinite scroll by reacting to actual DOM changes
   */
  #setupGridMutationObserver() {
    const { grid } = this.refs;

    if (!(grid instanceof HTMLElement)) {
      return;
    }

    this.#gridMutationObserver = new MutationObserver(mutations => {
      // Check if any cards were added and collect new product IDs
      const newProductIds = [];

      for (const mutation of mutations) {
        if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
          for (const node of mutation.addedNodes) {
            if (node instanceof HTMLElement && node.dataset.productId) {
              newProductIds.push(node.dataset.productId);
            }
          }
        }
      }

      if (newProductIds.length > 0) {
        // Dispatch custom event for Monetate tracking
        document.dispatchEvent(
          new CustomEvent("monetate:products-loaded", {
            detail: { productIds: newProductIds },
          })
        );

        // Use requestAnimationFrame to check after the browser finishes rendering
        requestAnimationFrame(() => {
          this.#updateLoadMoreButtonVisibility();
        });
      }
    });

    this.#gridMutationObserver.observe(grid, {
      childList: true,
    });
  }

  /**
   * Check if we've reached the last page and hide/show button accordingly
   */
  #updateLoadMoreButtonVisibility() {
    const { loadMoreButton, loadMoreContainer, grid } = this.refs;

    if (!(grid instanceof HTMLElement)) {
      return;
    }

    const lastPage = Number(grid.dataset.lastPage);

    // Query cards directly from the grid to get the current state after append
    const allCards = grid.querySelectorAll('[ref="cards[]"]');
    const lastCard = allCards[allCards.length - 1];
    const currentPage = lastCard instanceof HTMLElement ? Number(lastCard.dataset.page) : 0;

    // Hide button if we've reached the last page, otherwise re-enable it
    if (currentPage >= lastPage) {
      if (loadMoreContainer instanceof HTMLElement) {
        loadMoreContainer.style.display = "none";
      }
    } else {
      if (loadMoreButton instanceof HTMLButtonElement) {
        loadMoreButton.disabled = false;
        loadMoreButton.textContent = "Load More";
      }
    }
  }

  /**
   * Override parent's renderNextPage to update button visibility after loading
   */
  async renderNextPage() {
    await super.renderNextPage();
    this.#updateLoadMoreButtonVisibility();
  }

  /**
   * Handle load more button click
   */
  async handleLoadMore() {
    const { loadMoreButton } = this.refs;

    if (loadMoreButton instanceof HTMLButtonElement) {
      loadMoreButton.disabled = true;
      loadMoreButton.textContent = "Loading...";
    }

    // Trigger loading the next page (which will also update button visibility)
    await this.renderNextPage();
  }
}

if (!customElements.get("results-list")) {
  customElements.define("results-list", ResultsList);
}
